"use client";

import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../@/components/ui/chart";
import { Button } from "../@/components/ui/button";
import { useState, useMemo } from "react";
import { useGetDateOrDayWiseSalesQuery } from "../api/api";
import { Skeleton } from "../@/components/ui/skeleton";
import {
  generateDateRange,
  aggregateByMonth,
  useDecodedTokenResult,
} from "../lib/utils";

export function SalesChart() {
  const chartConfig = useMemo(
    () => ({
      totalSales: {
        label: "Total Sales: $",
        color: "#C9F7DC",
      },
    }),
    []
  );

  const options = [
    { name: "12 Months", value: "12months" },
    { name: "6 Months", value: "6months" },
    { name: "30 Days", value: "30days" },
    { name: "7 Days", value: "7days" },
  ];

  const { vendorId, role } = useDecodedTokenResult();
  const isVendorOrId = role === "ADMIN" ? undefined : vendorId;

  const [selectedOption, setSelectedOption] = useState("7days");

  const { data: salesData, isLoading } = useGetDateOrDayWiseSalesQuery({
    duration: selectedOption,
    vendorId: isVendorOrId,
  });

  const processData = (data: any[], rangeStart: Date, rangeEnd: Date) => {
    const interval = selectedOption === "30days" ? 3 : 1;
    const allDates = generateDateRange(rangeStart, rangeEnd, interval);
    const dataMap = new Map(
      data.map((item) => [
        new Date(item.period).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        }),
        item.totalSales,
      ])
    );

    return allDates.map((date) => {
      const formattedDate = date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });
      return {
        period: formattedDate,
        totalSales: dataMap.get(formattedDate) || 0,
      };
    });
  };

  const processedData = useMemo(() => {
    if (salesData && salesData.data) {
      const rangeStart = new Date();
      const rangeEnd = new Date();

      if (selectedOption === "12months") {
        rangeStart.setMonth(rangeStart.getMonth() - 11);
        return aggregateByMonth(salesData.data as any, rangeStart, rangeEnd);
      } else if (selectedOption === "6months") {
        rangeStart.setMonth(rangeStart.getMonth() - 5);
        return aggregateByMonth(salesData.data as any, rangeStart, rangeEnd);
      } else if (selectedOption === "30days") {
        rangeStart.setDate(rangeStart.getDate() - 30);
        return processData(salesData.data as any, rangeStart, rangeEnd);
      } else if (selectedOption === "7days") {
        rangeStart.setDate(rangeStart.getDate() - 6);
        return processData(salesData.data as any, rangeStart, rangeEnd);
      }

      return [];
    }
    return [];
  }, [salesData, selectedOption]);

  const chartMargins = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { top: 50, bottom: 70, left: 12, right: 12 }; // Adjusted default margins
    }

    const maxSales = Math.max(
      ...processedData.map((item) => item.totalSales || 0)
    );
    const minSales = Math.min(
      ...processedData.map((item) => item.totalSales || 0)
    );

    // Ensure minimum margins while still allowing for dynamic adjustment
    const dynamicTop = Math.max(50, Math.min(100 - maxSales / 1000, 70));
    const dynamicBottom = Math.max(70, Math.min(100 - minSales / 1000, 90));

    return { top: dynamicTop, bottom: dynamicBottom, left: 12, right: 12 };
  }, [processedData]);

  return (
    <div className="relative max-h-[84vh] min-h-[84vh] xxl:min-h-[76vh] xxl:max-h-[76vh] shadow-lg rounded-lg overflow-hidden">
      <Card className="border-none m-0 min-h-[100%] max-h-[100%] shadow-none p-0">
        <CardHeader className="p-0">
          <div className="inline-flex justify-between items-center p-2 border-none border-b-customBgGray">
            <CardTitle className="inline ml-1 text-lg">Sales Chart</CardTitle>
            <div>
              {options.map((option) => (
                <Button
                  key={option.value}
                  variant={
                    selectedOption === option.value ? "outline" : "ghost"
                  }
                  onClick={() => setSelectedOption(option.value)}
                >
                  {option.name}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-0">
          {isLoading ? (
            <Skeleton className="w-full h-[60vh]" />
          ) : (
            <ChartContainer
              className="w-full flex items-center justify-center px-7 flex-col"
              config={chartConfig}
            >
              <AreaChart
                key={selectedOption}
                accessibilityLayer
                data={processedData}
                margin={chartMargins}
                height={400}
                width={800}
              >
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="27.78%" stopColor="#C9F7DC" />
                    <stop offset="100%" stopColor="rgba(201, 247, 220, 0)" />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#f5f5f5" />
                <XAxis
                  dataKey="period"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={70}
                  interval={0}
                  className="font-helveticaMedium text-customNeutralGray text-[2.1vh]"
                  padding={{ left: 100, right: 100 }}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="dot" />}
                />
                <Area
                  dataKey="totalSales"
                  type="monotone"
                  fill="url(#colorSales)"
                  fillOpacity={2}
                  stroke="#bedbc9"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stackId="a"
                />
              </AreaChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
